import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, Grid, Avatar, Chip, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, List, ListItem, ListItemAvatar, ListItemText, CircularProgress,
  Tabs, Tab, TextField, InputAdornment, Stack, Divider,
} from '@mui/material';
import {
  LocalHospital as ClinicIcon, Person as DoctorIcon, LocationOn as LocationIcon,
  Phone as PhoneIcon, Search as SearchIcon, Verified as MyDoctorIcon,
  School as EducationIcon, MedicalServices as SpecialtyIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function SpecialtyChips({ specialities, size = 'small' }) {
  if (!specialities || specialities.length === 0) {
    return <Chip size={size} label="General Practice" variant="outlined" />;
  }
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {specialities.map((s, i) => (
        // A speciality can legitimately be listed twice, so the text alone is not a
        // safe key.
        <Chip key={`${s}-${i}`} size={size} label={s} color="secondary" variant="outlined" />
      ))}
    </Box>
  );
}

function MyDoctorBadge() {
  return (
    <Chip
      size="small"
      icon={<MyDoctorIcon sx={{ fontSize: 16 }} />}
      label="Your doctor"
      sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, border: '1px solid #86efac' }}
    />
  );
}

// ── Doctors tab ────────────────────────────────────────────────────────────
function DoctorsTab({ search }) {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const reqId = useRef(0);

  const fetchProviders = useCallback(async (term) => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/patient/providers`, {
        params: term ? { search: term } : {},
      });
      if (id === reqId.current) setProviders(res.data.providers || []);
    } catch {
      if (id === reqId.current) {
        setProviders([]);
        toast.error('Failed to load doctors');
      }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchProviders(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search, fetchProviders]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (providers.length === 0) {
    return (
      <Card sx={{ background: 'linear-gradient(145deg, #fffefb 0%, #faf6f0 100%)', maxWidth: 480, mx: 'auto' }}>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <DoctorIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {search.trim() ? `No doctors match “${search.trim()}”` : 'No doctors available yet'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {providers.map((p) => (
          // A doctor who works at two clinics is listed once per clinic, so the id
          // alone repeats.
          <Grid item xs={12} sm={6} md={4} key={`${p.id}-${p.clinic_id || ''}`}>
            <Card sx={{
              cursor: 'pointer', transition: 'all 0.2s', height: '100%',
              background: p.is_my_doctor
                ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                : 'linear-gradient(135deg, #fffefb 0%, #fff7ed 100%)',
              border: p.is_my_doctor ? '1px solid rgba(22, 163, 74, 0.3)' : '1px solid rgba(180, 83, 9, 0.12)',
              '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
            }} onClick={() => setSelected(p)}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><DoctorIcon /></Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={700} noWrap>{p.full_name}</Typography>
                    {p.is_my_doctor && <Box sx={{ mt: 0.5 }}><MyDoctorBadge /></Box>}
                  </Box>
                </Box>
                <Box sx={{ mb: 1.5 }}><SpecialtyChips specialities={p.speciality} /></Box>
                {p.clinic_name && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ClinicIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" noWrap>{p.clinic_name}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <DoctorDialog provider={selected} onClose={() => setSelected(null)} />
    </>
  );
}

function DoctorDialog({ provider, onClose }) {
  return (
    <Dialog open={Boolean(provider)} onClose={onClose} maxWidth="sm" fullWidth>
      {provider && (
        <>
          <DialogTitle sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{ bgcolor: 'primary.main' }}><DoctorIcon /></Avatar>
              <Box>
                {provider.full_name}
                {provider.is_my_doctor && <Box sx={{ mt: 0.5 }}><MyDoctorBadge /></Box>}
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            {provider.clinic_name && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <ClinicIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">{provider.clinic_name}</Typography>
              </Box>
            )}
            <Stack spacing={2}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <SpecialtyIcon fontSize="small" color="primary" />
                  <Typography variant="subtitle2" fontWeight={700}>Speciality</Typography>
                </Box>
                <SpecialtyChips specialities={provider.speciality} />
              </Box>
              {provider.information && (
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>About</Typography>
                  <Typography variant="body2" color="text.secondary">{provider.information}</Typography>
                </Box>
              )}
              {provider.education && provider.education.length > 0 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <EducationIcon fontSize="small" color="primary" />
                    <Typography variant="subtitle2" fontWeight={700}>Education</Typography>
                  </Box>
                  <List dense disablePadding>
                    {provider.education.map((e, i) => (
                      <ListItem key={`${e}-${i}`} disablePadding sx={{ pl: 0.5 }}>
                        <ListItemText primary={e} primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

// ── Clinics tab ────────────────────────────────────────────────────────────
function ClinicsTab({ search }) {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [providers, setProviders] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingProviders, setLoadingProviders] = useState(false);
  const reqId = useRef(0);

  const fetchClinics = useCallback(async (term) => {
    const id = ++reqId.current;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/patient/clinics`, {
        params: term ? { search: term } : {},
      });
      if (id === reqId.current) setClinics(res.data.clinics || []);
    } catch {
      if (id === reqId.current) {
        setClinics([]);
        toast.error('Failed to load clinics');
      }
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => fetchClinics(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search, fetchClinics]);

  const viewClinic = async (clinic) => {
    setSelectedClinic(clinic);
    setDialogOpen(true);
    setLoadingProviders(true);
    try {
      const res = await axios.get(`${API_URL}/api/v1/patient/clinics/${clinic.id}/providers`);
      setProviders(res.data.providers || []);
    } catch {
      setProviders([]);
    } finally {
      setLoadingProviders(false);
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (clinics.length === 0) {
    return (
      <Card sx={{ background: 'linear-gradient(145deg, #fffefb 0%, #faf6f0 100%)', maxWidth: 480, mx: 'auto' }}>
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          <ClinicIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {search.trim() ? `No clinics match “${search.trim()}”` : 'No clinics available yet'}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {clinics.map((clinic) => (
          <Grid item xs={12} sm={6} md={4} key={clinic.id}>
            <Card sx={{
              cursor: 'pointer', transition: 'all 0.2s', height: '100%',
              background: 'linear-gradient(135deg, #fffefb 0%, #fff7ed 100%)',
              border: '1px solid rgba(180, 83, 9, 0.12)',
              '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
            }} onClick={() => viewClinic(clinic)}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}><ClinicIcon /></Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={700}>{clinic.name}</Typography>
                    <Chip size="small" label={clinic.type || 'clinic'}
                      color="primary" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                  </Box>
                </Box>
                {clinic.address && (
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <LocationIcon fontSize="small" color="action" sx={{ mt: 0.3 }} />
                    <Typography variant="body2" color="text.secondary">{clinic.address}</Typography>
                  </Box>
                )}
                {clinic.phone && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">{clinic.phone}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                  <DoctorIcon fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight={600}>
                    {clinic.provider_count} {clinic.provider_count === 1 ? 'doctor' : 'doctors'} available
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700 }}>
          {selectedClinic?.name}
        </DialogTitle>
        <DialogContent dividers>
          {selectedClinic?.address && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <LocationIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">{selectedClinic.address}</Typography>
            </Box>
          )}
          {selectedClinic?.phone && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PhoneIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">{selectedClinic.phone}</Typography>
            </Box>
          )}
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Available Doctors</Typography>
          {loadingProviders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
          ) : providers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">No doctors listed at this time.</Typography>
          ) : (
            <List>
              {providers.map((p) => (
                <ListItem key={`${p.id}-${p.clinic_id || ''}`} alignItems="flex-start" sx={{ px: 0 }}>
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main' }}><DoctorIcon /></Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography component="span" fontWeight={700}>{p.full_name}</Typography>
                        {p.is_my_doctor && <MyDoctorBadge />}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <SpecialtyChips specialities={p.speciality} />
                        {p.information && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                            {p.information}
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────
export default function Clinics() {
  const [tab, setTab] = useState(0);
  const [search, setSearch] = useState('');

  const handleTabChange = (_e, v) => {
    setTab(v);
    setSearch('');
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={700}
          sx={{ fontFamily: '"Cormorant Garamond", Georgia, serif', color: '#431407', mb: 1 }}>
          Find Care
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560 }}>
          Search for a doctor by name or speciality, or browse clinics and the doctors accepting patients there.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 3, alignItems: { sm: 'center' } }}>
        <Tabs value={tab} onChange={handleTabChange} sx={{ minHeight: 0 }}>
          <Tab icon={<DoctorIcon fontSize="small" />} iconPosition="start" label="Doctors" sx={{ minHeight: 0, py: 1 }} />
          <Tab icon={<ClinicIcon fontSize="small" />} iconPosition="start" label="Clinics" sx={{ minHeight: 0, py: 1 }} />
        </Tabs>
        <TextField
          size="small"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={tab === 0 ? 'Search doctors by name or speciality…' : 'Search clinics by name or location…'}
          sx={{ maxWidth: { sm: 380 }, ml: { sm: 'auto' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
            ),
          }}
        />
      </Box>

      {tab === 0 ? <DoctorsTab search={search} /> : <ClinicsTab search={search} />}
    </Box>
  );
}
